import async from 'async';
import _ from 'lodash';

import {
  ManagerAction,
  ManagerStatus,
  ProgressParams,
  SubTask,
  Task,
  TaskEvents,
  TaskManagerList,
  TaskManagerOptions,
  TaskStatus,
} from '@/renderer/scripts/utils/TaskManager/types';
import EventEmitter from '@/common/EventEmitter';
import { TaskStatusUtil } from '@/renderer/scripts/utils/TaskManager/utils/TaskUtils';
import { BaseTaskManagerHook } from '@/renderer/scripts/utils/TaskManager/types/hooks';
import { downloadStreamManager, uploadStreamManager } from '@/renderer/scripts/utils/StreamManager';
import { SystemSetting } from '@/renderer/scripts/utils/StreamManager/utils/SystemSetting';
import { TRANSFER_LIMIT } from '@/renderer/scripts/utils/ConstantConfig';
import { IS_BROWSER } from '@/common/constant';
import { RangeLimit } from '@/renderer/scripts/utils/storage';

/**
 * @description 批量任务管理
 * 对外事件:
 * 1. list-update 任务队列更新事件
 * 2. task-finish 单个任务完成时触发事件
 * 3. subtask-finish 单个子任务完成时触发事件
 * @method
 * @export
 * @class TaskManager
 * @extends {EventEmitter}
 */
export default class BaseTaskManager<T> extends EventEmitter {
  /**
   * @description 正在执行的任务队列, 注意：这里的list是指Task任务队列，subTask不在这里
   * @private
   * @type {Array<Task>}
   * @memberof TaskManager
   */
  private runningList: Array<Task<T>> = [];

  /**
   * @description 等待中的任务队列
   * @private
   * @type {Array<Task>}
   * @memberof TaskManager
   */
  private waitingList: Array<Task<T>> = [];

  /**
   * @description 已完成的任务队列
   * @private
   * @type {Array<Task>}
   * @memberof TaskManager
   */
  private finishList: Array<Task<T>> = [];

  /**
   * @description 全部任务队列
   * @private
   * @type {Array<Task>}
   * @memberof TaskManager
   */
  private list: Array<Task<T>> = [];

  // 最大额度限制
  private batchLimit = 5;

  // 是否处于监听速度状态
  private _speedWatching = false;
  // 监控日志 log bytes/s
  private _speedLog: number[] = [];

  /**
   * 根据系统设置设置manager的逻辑处理
   * @param batchLimit
   */
  public setBatchLimit = (batchLimit: number) => {
    this.batchLimit = batchLimit;
  };
  public _resetDefaultBatchLimit() {
    this.batchLimit = TRANSFER_LIMIT.upload.batchLimit;
  }

  //
  /**
   * @description 自动统计任务数量[runningList/waitingList/finishList]的摘要信息(包括Task和可能存在的subTask)
   * 作用:为了方便对整个传输过程进行数量限制
   *
   * @private
   */
  private runningSubTaskMap = new Map<string, SubTask<T>>();

  // 获取正在运行的subTaskMap数据
  public getRunningSubTaskMapSize = () => this.runningSubTaskMap.size;

  public insertSubTaskInMap = (subTask: SubTask<T>) => {
    this.runningSubTaskMap.set(subTask.id, subTask);
  };

  public deleteSubTaskInMap = (subTask: SubTask<T>) => {
    this.runningSubTaskMap.delete(subTask.id);
  };

  public clearSubTaskInMap = (taskId: string) => {
    // 清理同个任务id下的所有子任务
    const values = this.runningSubTaskMap.values();
    for (let i = 0; i < this.runningSubTaskMap.size; i++) {
      const subTask = values.next().value;
      if (subTask?.taskId === taskId) {
        this.runningSubTaskMap.delete(subTask.id);
      }
    }
  };

  public getTotalSpeed = (taskId: string) => {
    let totalSpeed = 0;
    this.runningSubTaskMap.forEach((subTask: SubTask<T>) => {
      const isRunning = subTask.ins && subTask.ins.isRunning() && subTask.status !== TaskStatus.paused;
      if (isRunning && taskId === subTask.taskId) {
        totalSpeed += subTask.speed;
      }
    });
    return totalSpeed;
  };

  // 任务管理器状态
  private status: ManagerStatus;
  private action: ManagerAction = ManagerAction.none;

  public onStart: (param: BaseTaskManagerHook<T>) => void | Promise<void> | undefined;

  public onPause: Function | undefined;

  public onFinish: Function | undefined;

  public onCancel: Function | undefined;

  public onError: Function | undefined;

  public onRetry: Function | undefined;

  /**
   * @description 更新当前队列信息
   * @private
   * @type {Function}
   * @memberof TaskManager
   */
  private emitListUpdate: Function;
  private emitTaskUpdate: Function;

  private _type: string;

  constructor({
    type,
    batchLimit = 5,
    onStart,
    onRetry,
    onPause,
    onCancel,
    onFinish,
    onError,
    onStatusChange,
  }: TaskManagerOptions<T>) {
    super();

    this._type = type || '';
    this.runningList = [];
    this.waitingList = [];
    this.finishList = [];
    this.batchLimit = batchLimit;

    this.onStart = onStart;
    this.onRetry = onRetry;
    this.onPause = onPause;
    this.onCancel = onCancel;
    this.onFinish = onFinish;
    this.onError = onError;

    // 默认是闲时状态
    this._setStatus(ManagerStatus.idle);
    this.status = ManagerStatus.idle;
    this.emitListUpdate = _.throttle(() => {
      this.emit(TaskEvents.listUpdate, {
        running: this.runningList,
        waiting: this.waitingList,
        finish: this.finishList,
        list: [...this.runningList, ...this.waitingList, ...this.finishList],
      });
    }, 400);

    /**
     * 单任务状态更新时触发
     * 任务的进度更新不会触发，但文件夹进度更新会触发
     */
    this.emitTaskUpdate = ({ task, status }: { task: Task<T>; status: TaskStatus }) => {
      this.emit(TaskEvents.taskUpdate, { task, status });
    };
  }

  /**
   * @description 添加批量任务
   * 添加任务队列，并为每个任务添加 status 和 uuid。
   * @param {Task} _task 要添加的任务
   * @param {{
   *       insertBefore: boolean;
   *     }} [_opt={ insertBefore: true }]
   * @memberof TaskManager
   */
  public addTask(
    task: Task<T>,
    _opt: {
      insertBefore: boolean;
    } = { insertBefore: true },
  ): void {
    this.emitTaskUpdate({ task, status: TaskStatus.waiting });
    if (_opt.insertBefore) {
      this.waitingList.unshift(task);
    } else {
      this.waitingList.push(task);
    }
  }

  // 根据系统配置修改任务管理器的配置
  public updateSettingBySystemSetting() {
    let rangeLimit: RangeLimit | undefined;
    if (IS_BROWSER) return;
    const taskManagerType = this._type;
    if (taskManagerType === 'upload' || taskManagerType === 'download') {
      // speedLimit true->开启限速 false->不限速;
      // batchAuto 自动模式
      const rangeLimit = SystemSetting.getSystemTransferSetting(taskManagerType);
      const { /* 是否限速 */ speedLimit, /* 最优模式 */ batchAuto, /* 并发数量 */ batchLimit } = rangeLimit;
      logger.ui(`[UpdateSystemSetting] ${taskManagerType} setting changed: ${JSON.stringify(rangeLimit)}`);
      if (batchAuto) {
        this._resetDefaultBatchLimit();
      } else {
        this.setBatchLimit(batchLimit);
      }
    }
    return rangeLimit;
  }
  /**
   * @description 尝试从任务队列中取出可执行的任务，直到补齐了到任务的最大值为止。
   * TODO 该方法会更新任务管理器的状态，若至少启动了一个任务，则会修改状态为运行中(任务管理器状态修改太频繁了）
   * 该方法每次都会检查，若检查到 运行时文件/等待文件都是0，则调用结束生命周期函数：onFinish
   *
   * 如果有可执行的任务，则全局状态为 running。
   * 如果没有，则全局状态为 finish。
   * 场景：
   * 当任务 start、cancel、pause、error 、finish、 restart 会调用（会来到这里再次从waitingList中读取数据来运行）
   *
   * @memberof TaskManager
   */
  private _runTasks(): void {
    if (this.status === ManagerStatus.loading) {
      return;
    }
    // 当 限制范围 > 运行数据 的时候，才会继续运行
    while (this.batchLimit > this.runningSubTaskMap.size) {
      const task: Task<T> | undefined = this.getTask({ available: true });
      if (task) {
        const taskManagerType = this._type;
        const rangeLimit = this.updateSettingBySystemSetting();
        // type 有值再进来
        /* if (!IS_BROWSER) {
          if (taskManagerType === 'upload' || taskManagerType === 'download') {
            // 若是[上传最优模式]/[下载最优模式]，则开始算速度
            if (!rangeLimit?.speedLimit) {
              // [0s,10s]是测速时段: 此时内部不限速，offset=0
              // [10s,end]不是测速阶段，此时内部限速，测速阶段结果最大值 = offset + limit速度
              this._speedLog = [];
              this._speedWatching = true;
              if (taskManagerType === 'upload') {
                uploadStreamManager.startDetectSpeed();
              }
              if (this._type === 'download') {
                downloadStreamManager.startDetectSpeed();
              }
              setTimeout(() => {
                this._speedWatching = false;
                // 防御性判断 若任务已结束，则需要return，退出栈
                if (!TaskStatusUtil.isRunning(task.status)) {
                  return;
                }
                // 防御性判断 若最优模式已结束，则需要return，退出栈
                const { speedLimit } = SystemSetting.getSystemTransferSetting(taskManagerType);
                if (speedLimit) return;
                // 防御性判断 若已经没有this._speedLog 就退出栈
                const hasSPeed = this._speedLog.some(speedItem => speedItem > 0);
                if (!hasSPeed) return;
                const maxSpeedBytes = Math.max(...this._speedLog);
                if (taskManagerType === 'upload') {
                  uploadStreamManager.stopDetectSpeed(maxSpeedBytes);
                }
                if (this._type === 'download') {
                  downloadStreamManager.stopDetectSpeed(maxSpeedBytes);
                }
              }, 10 * 1000);
            }
          }
        } */

        this.updateTaskStatus({ task, status: TaskStatus.ready });
        this._runOneTask(task);
      } else {
        if (this.runningList.length === 0 && this.waitingList.length === 0) {
          this._setStatus(ManagerStatus.idle);
          this.onFinish && this.onFinish({ list: this.finishList });
        }
        break;
      }
    }
  }

  /**
   * @description 运行一个指定参数内的任务，任务需要提前变更状态为running。
   *
   * @memberof TaskManager
   */
  private _runOneTask(task: Task<T>): void {
    if (!TaskStatusUtil.isReady(task.status)) return;

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    this.emitReady(task);
    // 在upload特殊处理，但是在其他的过程中，依然转换为running
    if (this._type !== 'upload' || task.isDir === true) {
      this._setStatus(ManagerStatus.processing);
      this.updateTaskStatus({ task, status: TaskStatus.running });
    }
    this.onStart &&
      this.onStart({
        task,
        // runningSubTaskMap: self.runningSubTaskMap,
        onProgress(params: ProgressParams) {
          if (self._speedWatching) {
            params?.speed !== undefined && self._speedLog.push(Math.round(params?.speed));
          }
          self.emitProgress(task, params);
        },
        onChecking() {
          // 任务类型是文件夹，不转换为checking
          task?.isDir !== true && self.emitChecking(task);
        },
        onPreparing() {
          // 任务类型是文件夹，不转换为preparing
          task?.isDir !== true && self.emitPreparing(task);
        },
        onSubTaskFinish(subTask: SubTask<T>) {
          self.emit('subtask-finish', subTask);
        },
        /* onSubTaskStart(subTask: SubTask<T>) {
        // 当上方没有出错的时候，添加子任务数量
        self.insertRunningSubTask(subTask);
      },*/
        onFinish() {
          setTimeout(() => {
            self.emitFinish(task);
          }, 0);
        },
        onError(err: any) {
          self.emitError(task, err);
        },
        onReady() {
          self.emitStart(task);
        },
        onUnReady(err: any) {
          self.emitError(task, err);
        },
      });
  }

  /**
   * @description 启动全部任务队列
   */
  public start(): void {
    this._fixStatusByList();
    try {
      this._runTasks();
    } finally {
      this._fixStatusByList();
    }
  }
  /**
   * 重启全部任务队列
   * @description 通过外界action调用
   * @memberof TaskManager
   */
  public restart(): void {
    this._fixStatusByList();
    const list = [...this.runningList, ...this.waitingList];
    const asyncPromise = async.forEachLimit(list, 16, (task, next) => {
      if (!TaskStatusUtil.isRunning(task.status)) {
        const hasErr = TaskStatusUtil.isError(task.status) || (task.errorNum && task.errorNum > 0);
        // eslint-disable-next-line no-unused-expressions
        hasErr ? this.retryTask(task.taskId) : this.restartTask(task.taskId);
      }
      next();
    });
    asyncPromise.finally(() => {
      this._fixStatusByList();
    });
  }
  /**
   * @description 重启某个任务
   * @memberof TaskManager
   */
  public restartTask(taskId: string): void {
    this._fixStatusByList();
    try {
      const task = this.getTask({ taskId });
      task && this.emitRestart(task);
    } catch (e) {
      this._fixStatusByList();
    }
  }

  /**
   * @description 暂停全部任务队列
   * 将队列状态设置为 paused。
   * 停止所有运行中的任务
   * @memberof TaskManager
   */
  public pause(): void {
    const action = ManagerAction.pauseAll;
    if (this._isLoading()) {
      /* i18n-ignore*/
      this._reject(action, `TaskManager仍然在Loading中`);
      return;
    }
    this._setStatus(ManagerStatus.loading, action);
    const list = [...this.runningList, ...this.waitingList];
    const asyncPromise = async.forEachLimit(list, 16, (task, next) => {
      TaskStatusUtil.isRunning(task.status) && this.pauseTask(task.taskId, { action });
      next();
    });
    asyncPromise.finally(() => {
      this._fixStatusByList();
    });
  }
  /**
   * 停止某个任务
   * @param taskId
   * @param options 若存在option.action，则以暂停全部处理
   */
  public pauseTask(taskId: string, options?: { action: ManagerAction }): void {
    if (options?.action !== ManagerAction.pauseAll) {
      const action = ManagerAction.pauseOne;
      if (this._isLoading()) {
        /* i18n-ignore*/
        this._reject(action, `TaskManager仍然在Loading中`);
        return;
      }
      this._setStatus(ManagerStatus.loading, action);
    }
    try {
      const task = this.getTask({ taskId });
      task && this.emitPause(task, options);
    } finally {
      if (options?.action !== ManagerAction.pauseAll) {
        this._fixStatusByList();
      }
    }
  }

  /**
   * @description 取消/删除全部任务队列
   * @param {('running' | 'finish')} type 默认删除正在进行中的队列
   * running 表示取消正在进行中的队列
   * finish 表示删除已完成的队列
   * all 表示删除所有队列
   * @memberof TaskManager
   */
  public cancel(type: 'running' | 'finish' | 'all' = 'running'): void {
    const action = ManagerAction.cancelAll;
    if (this._isLoading()) {
      /* i18n-ignore*/
      this._reject(action, `TaskManager仍然在Loading中`);
      return;
    }
    this._setStatus(ManagerStatus.loading, action);

    let list = [...this.runningList, ...this.waitingList];
    if (type === 'finish') {
      list = [...this.finishList];
    } else if (type === 'all') {
      list = [...list, ...this.finishList];
    }
    const asyncPromise = async.forEachLimit(list, 16, (task, next) => {
      task && this.cancelTask(task.taskId, { action });
      next();
    });
    asyncPromise.finally(() => {
      this._fixStatusByList();
    });
  }

  /**
   * @description 取消某个任务
   * @memberof TaskManager
   */
  public cancelTask(taskId: string, options?: { action: ManagerAction }): void {
    if (options?.action !== ManagerAction.cancelAll) {
      const action = ManagerAction.cancelOne;
      if (this._isLoading()) {
        /* i18n-ignore*/
        this._reject(action, `TaskManager仍然在Loading中`);
        return;
      }
      this._setStatus(ManagerStatus.loading, action);
    }
    const task = this.getTask({ taskId });
    task && this.emitCancel(task, options);
  }

  /**
   * @description 重试某个任务
   * @memberof TaskManager
   */
  public retryTask(taskId: string): void {
    const action = ManagerAction.retryOne;
    if (this._isLoading()) {
      /* i18n-ignore*/
      this._reject(action, `TaskManager仍然在Loading中`);
      return;
    }
    this._setStatus(ManagerStatus.loading, action);
    const task = this.getTask({ taskId });
    if (this.onRetry && task) {
      this.onRetry({
        task,
        onProgress: (params: ProgressParams) => {
          this.emitProgress(task, params);
        },
        onFinish: () => {
          this.emitRestart(task);
        },
      });
    } else {
      task && this.emitRestart(task);
    }
  }

  public getList(): TaskManagerList<T> {
    return {
      running: this.runningList,
      waiting: this.waitingList,
      finish: this.finishList,
      list: [...this.waitingList, ...this.runningList, ...this.finishList],
    };
  }

  public setList({
    waitingList,
    runningList,
    finishList,
  }: {
    waitingList: Task<T>[];
    runningList: Task<T>[];
    finishList: Task<T>[];
  }) {
    runningList && (this.runningList = runningList);
    waitingList && (this.waitingList = waitingList);
    finishList && (this.finishList = finishList);
    this.list = [...this.waitingList, ...this.runningList, ...this.finishList];
    this.emitListUpdate();
  }

  /**
   * @description 获取某个任务
   * @param {({
   *     taskId?: string | undefined;
   *     available?: boolean | undefined;
   *   })} {
   *     taskId = '',
   *     available = true,
   *   } 传入 taskId 则获取 taskId 对应的任务，传入 available 为 true 则表示从当前等待任务队列中推出一个任务
   * @return {*}  {Promise<Task>}
   * @memberof TaskManager
   */
  private getTask({ taskId = '', available = true }) {
    let task!: Task<T> | undefined;
    if (taskId) {
      const runningIndex = this.runningList.findIndex(item => item.taskId === taskId);
      const waitingIndex = this.waitingList.findIndex(item => item.taskId === taskId);
      const finishIndex = this.finishList.findIndex(item => item.taskId === taskId);
      if (runningIndex > -1) {
        task = this.runningList[runningIndex];
      } else if (waitingIndex > -1) {
        task = this.waitingList[waitingIndex];
      } else if (finishIndex > -1) {
        task = this.finishList[finishIndex];
      }

      if (TaskStatusUtil.isError((task as Task<T>).status)) {
        // runningIndex > -1 && this.runningSubTaskMap.delete(this.runningList[runningIndex].id);
        runningIndex > -1 && this.runningList.splice(runningIndex, 1);
        waitingIndex > -1 && this.waitingList.splice(waitingIndex, 1);
        finishIndex > -1 && this.finishList.splice(finishIndex, 1);
      }
      return task;
    }

    if (available && this.waitingList.length > 0) {
      // 锁定状态，不允许找出文件
      if (this.status === ManagerStatus.loading) {
        return;
      }
      const index = this.waitingList.findIndex(item => item.status === TaskStatus.waiting);
      index > -1 && ([task] = this.waitingList.splice(index, 1));
      return task;
    }
    return task;
  }

  /**
   * @description 当有任务启动时触发
   * @private
   * @memberof TaskManager
   */
  private emitStart(task: Task<T>) {
    this.updateTaskStatus({ task, status: TaskStatus.running });
  }

  /**
   * 有任务陷入IO处理进度时触发
   * @param task
   * @private
   */
  private emitReady(task: Task<T>) {
    this.updateRunningList(task, 'start');
    this.updateTaskStatus({ task, status: TaskStatus.ready });
  }
  private emitPreparing(task: Task<T>) {
    this.updateTaskStatus({ task, status: TaskStatus.preparing });
  }
  private emitChecking(task: Task<T>) {
    this.updateTaskStatus({ task, status: TaskStatus.checking });
  }

  /**
   * @description 当有任务更新时触发
   * @private
   * @memberof TaskManager
   */
  private emitProgress(task: Task<T>, params: ProgressParams = {}) {
    let isDirUpdate = false;
    // 文件夹更新
    if (params.progressText) {
      task.progressText = params.progressText;
      isDirUpdate = true;
    }
    if (params.speed !== undefined) {
      task.speed = params.speed;
    }
    if (params.ioPercent !== undefined) {
      task.ioPercent = params.ioPercent;
    }
    // 监听到子任务的状态改变
    const isSubTaskStatusUpdate = !!params.subTaskStatus;
    this.updateTaskStatus({ task, isDirUpdate, isSubTaskStatusUpdate });
  }

  /**
   * @description 当有任务完成时触发
   * @private
   * @memberof TaskManager
   */
  private emitFinish(task: Task<T>) {
    logger.ui(`[TaskManage] ${task.action} task "${task.title}" finished. ${task.getListLength()} in total.`);
    this.updateRunningList(task, 'finish');
    this.updateTaskStatus({ task, status: TaskStatus.success });
    this._runTasks();

    this.emit(TaskEvents.taskFinish, task);
  }

  /**
   * @description 当有任务报错时触发
   * @private
   * @memberof TaskManager
   */
  private emitError(task: Task<T>, err: any) {
    task.error = err;
    this.updateRunningList(task, 'error');
    this.updateTaskStatus({ task, status: TaskStatus.error });
    this._runTasks();

    this.emit(TaskEvents.taskFinish, task);
  }

  /**
   * @description 当有任务重启时触发
   * 1. 更新任务状态 status（更新为 waiting，并不是直接启动，需要排队）
   * 2. 执行下一个任务
   * @private
   * @memberof TaskManager
   */
  private emitRestart(task: Task<T>) {
    this.updateRunningList(task, 'waiting');
    this.updateTaskStatus({ task, status: TaskStatus.waiting });
    this._fixStatusByList();
    this._runTasks();
  }

  /**
   * @description 当有任务暂停时触发
   * 1. 触发任务 onPause
   * 2. 更新当前运行中列表 currentList
   * 3. 更新任务状态 status
   * 4. 执行下一个任务
   * @private
   * @memberof TaskManager
   */
  private emitPause(task: Task<T>, options?: { action: ManagerAction }) {
    this.updateTaskStatus({ task, status: TaskStatus.paused });
    this.updateRunningList(task, 'pause');
    // 这里onPause 必须放之后处理
    this.onPause && this.onPause(task);
    if (options?.action !== ManagerAction.pauseAll) {
      this._fixStatusByList();
      this._runTasks();
    }
  }

  /**
   * @description 当有任务取消时触发
   * 1. 触发任务 onCancel
   * 2. 更新当前运行中列表 currentList
   * 3. // 更新任务队列，删除该任务
   * 4. 更新任务状态 status，设置为 canceled
   * 5. 执行下一个任务
   * @private
   * @memberof TaskManager
   */
  private async emitCancel(task: Task<T>, options?: { action: ManagerAction }) {
    this.updateRunningList(task, 'cancel');
    this.updateTaskStatus({ task, status: TaskStatus.canceled });
    // 这里onCancel 必须放之后处理
    this.onCancel && this.onCancel(task);
    // 若是取消全部，就不再此处解锁，而应该由cancelAll解锁
    if (options?.action !== ManagerAction.cancelAll) {
      this._fixStatusByList();
      this._runTasks();
    }
  }

  /**
   * @description 更新任务状态 status（或者进度信息）
   * 任务状态更新时，触发 list-update，返回 { list, currentList }
   * @private
   * @param {Task} [task]
   * @param {StatusType} [status]
   * @param {boolean} [silent]
   * @memberof TaskManager
   */
  private updateTaskStatus(params: {
    task: Task<T>;
    status?: TaskStatus;
    silent?: boolean;
    isDirUpdate?: boolean;
    isSubTaskStatusUpdate?: boolean;
  }): void {
    const { task, status, silent, isDirUpdate, isSubTaskStatusUpdate } = params;
    // taskStatus变化 | 文件夹里数量变化 | 子任务状态变化 | 下载任务进度变化时 推给DB更新队列
    const statusChanged = status && status !== task.status;
    const downloadTaskProgress = task.action === 'download' && !status;
    const needUpdateDB = statusChanged || isDirUpdate || isSubTaskStatusUpdate || downloadTaskProgress;
    if (task && status) {
      task.status = status;
    }

    needUpdateDB && this.emitTaskUpdate({ task, status });

    !silent && this.emitListUpdate();
  }

  /**
   * @description 更新运行队列
   * @private
   * @param {Task} task
   * @param {string} action
   * start: 启动任务 | finish 完成任务 | pause 暂停任务 | cancel 取消任务
   * @memberof TaskManager
   */
  private updateRunningList(
    task: Task<T>,
    action: 'start' | 'waiting' | 'pause' | 'finish' | 'cancel' | 'error',
  ): void {
    const taskExist = this.runningList.find(item => item.taskId === task.taskId);
    if (action === 'start') {
      !taskExist && this.runningList.push(task);
      // 由于每一个Task都会对应创建一个唯一的subTask进行上传，所以这里不再追加
      // this.insertRunningSubTask(task);
    } else if (action === 'waiting') {
      if (TaskStatusUtil.isFinished(task.status)) {
        !taskExist && this.waitingList.unshift(task);
      }
    } else if (['error', 'pause', 'finish'].includes(action)) {
      const index = this.runningList.findIndex(item => item.taskId === task.taskId);
      if (index > -1) {
        const task = this.runningList.splice(index, 1)[0];
        // this.deleteRunningSubTask(task.id);
        switch (action) {
          case 'error':
          case 'pause': {
            this.waitingList.unshift(task);
            break;
          }
          case 'finish': {
            this.finishList.unshift(task);
            break;
          }
          default:
            break;
        }
      }
    } else if (action === 'cancel') {
      const runningIndex = this.runningList.findIndex(item => item.taskId === task.taskId);
      const waitingIndex = this.waitingList.findIndex(item => item.taskId === task.taskId);
      const finishIndex = this.finishList.findIndex(item => item.taskId === task.taskId);

      // 先清理掉多余的map中的数据
      // runningIndex > -1 && this.deleteRunningSubTask(this.runningList[runningIndex].id);
      runningIndex > -1 && this.runningList.splice(runningIndex, 1);
      waitingIndex > -1 && this.waitingList.splice(waitingIndex, 1);
      finishIndex > -1 && this.finishList.splice(finishIndex, 1);
    }
  }

  static getTaskId(prefix = ''): string {
    return `${prefix}${new Date().getTime()}${Math.random() * 1000}`;
  }

  /**
   * @description 是否存在未完成的任务
   * @return {*}  {boolean}
   * @memberof TaskManager
   */
  public existUnfinishedTask(): boolean {
    return [...this.runningList, ...this.waitingList].length > 0;
  }
  // 根据当前runningList是否还存在，进行状态更新
  private _fixStatusByList() {
    if (this.runningList?.length > 0) {
      this._setStatus(ManagerStatus.processing);
    } else {
      this._setStatus(ManagerStatus.idle);
    }
  }
  // 更新状态
  private _setStatus(status: ManagerStatus, action?: ManagerAction) {
    this.status = status;
    if (status === ManagerStatus.loading) {
      this.action = action as ManagerAction;
    } else {
      this.action = ManagerAction.none;
    }
  }
  // 任务管理器是否正在处理内部事情
  private _isLoading(): boolean {
    return this.status === ManagerStatus.initializing || this.status === ManagerStatus.loading;
  }
  // 内部错误,打印日志处理
  private _reject(action: ManagerAction, reason: string) {
    logger.ui.error(`[TaskManager]`, `action ${action} be reject ,reason is ${reason}`);
  }
}
